/**
 * "The numbers below are frozen because the PLAN is dead."
 *
 * When an account's base plan is not live, Zoom's /accounts/{id}/plans/usage endpoint returns NOTHING for
 * it. Every licence / seat / utilization figure still sitting in the mirror is frozen at the moment the
 * trial (or the paid term) ended and can never refresh again. ISALAMIC BANK OF THAILAND renders
 * "15 purchased / 1 used" on a plan that died on 2026-05-28 while Zoom's own console says Basic with no
 * assignable licences. Rendering those figures unlabelled IS the bug — so this notice is not chrome.
 *
 * ONE LINE: what happened, and — on paid_expired only — what to do. Nothing else.
 *
 * ⚠️ THE DATA-AGE TRAILER IS GONE, and its `plan_data_age_days` prop with it. The notice used to append
 * "Zoom last refreshed this plan {N} days ago". On the real prod rows ZPCD re-fetches the dead plan row
 * every single day, so N is 0 — the panel rendered "…these numbers will never update again. Zoom last
 * refreshed this plan 0 days ago", which is a useless sentence AND a direct contradiction of the one in
 * front of it. A notice that argues with itself teaches the operator to skip notices. (The freshness
 * question the trailer was answering does not need answering here: UsageTab still suppresses its
 * "Last synced" caption on a frozen plan, so nothing on the tab certifies the stale figures as fresh.)
 *
 * A DIFFERENT AXIS FROM THE RETIRED NOTICE. Retirement = Zoom stopped LISTING the account. Plan death =
 * the PLAN ended. An account can be both, and then BOTH notices render — they answer different questions
 * ("we stopped receiving data" vs "the plan itself is over") and collapsing them would drop one fact.
 *
 * TONE, on paid_expired: this is the account that may be an INCORRECTLY-expired paying customer (QUALICA,
 * with an open Zoom support case). The copy says what ZOOM REPORTS and sends the reader to verify. It
 * never tells them the customer is dead. That survived the copy cut; it is the one thing here that must.
 *
 * Renders NOTHING for `live` / `unknown` / an absent state — we only suppress a figure we can PROVE stale.
 *
 * ⚠️ 2026-09-18 (round 3, ruling 2) — `plan_badge` and `base_type`, both OPTIONAL, gain a THIRD branch.
 * `plan_state` alone can read not-live while the 0030 ruling's own `plan_badge` says the account is
 * genuinely Active (≈39 accounts on the local mirror: 35 Zoom-Phone-only base codes with no meeting plan
 * at all, plus a few whose fixed-end line is still in date while Zoom's `status` claim on that SAME line
 * says otherwise). The figures below ARE STILL last-known either way — nothing here changes THAT fact —
 * only the FALSE "the plan has ended" clause does, and the tone is always warn, NEVER the paid_expired
 * danger tone, because the account IS Active under the ruling. Without `plan_badge` (an old/stripped
 * payload) this component renders EXACTLY what it always did, from `plan_state` alone — the new branch
 * only ever fires on the literal string `"active"`, never re-derived, never guessed.
 */

import { useTranslation } from "react-i18next";
import type { TKey } from "@/i18n/keys";
import { isPlanNotLive, planStateOf } from "@/lib/planState";
import type { PlanBadge, PlanState } from "@/api/types";

export interface PlanFreezeNoticeProps {
  plan_state?: PlanState | null | undefined;
  /** The 0030 ruling's verdict (round 3, ruling 2). Present + exactly `"active"` reroutes to the
   *  Active-not-live wording below; anything else (absent, null, free/expired/unknown) leaves this
   *  component's behaviour byte-identical to before `plan_badge` existed. */
  plan_badge?: PlanBadge | null | undefined;
  /** The real base plan SKU — only consulted when `plan_badge === "active"`, to tell the Zoom-Phone-only
   *  wording (variant a) from every other Active-not-live account (variant b). */
  base_type?: string | null | undefined;
  className?: string | undefined;
}

/** The Zoom-Phone-only placeholder base codes — same shape as the API's `_PLACEHOLDER_BASE_RE`
 *  (service.py ~483). An account on one of these has NO meeting plan at all. */
const PHONE_ONLY_BASE_RE = /^PLANCODE_720[01]$/;

/** Shared so the Active-not-live branch and the classic FREEZE_KEY branch render byte-identical markup —
 *  a hand-duplicated class string is exactly how the two would quietly drift apart. */
function noticeClassName(tone: "danger" | "warn", className: string | undefined): string {
  return [
    "rounded-(--radius-sm) border px-3 py-2 text-(length:--text-sm) text-(--text)",
    tone === "danger"
      ? "border-(--danger) bg-(--danger-subtle)"
      : "border-(--warn) bg-(--warn-subtle)",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * The i18n key per not-live state.
 *
 * Typed as PARTIAL over the full PlanState union rather than as a closed map of the four dead states, so
 * that the lookup below is TOTAL and needs no cast. That is not a style preference — it is a fail-safe.
 * If `isPlanNotLive` ever admits a state that has no sentence here (exactly what happens if someone adds
 * `unknown` to the not-live set), a cast would sail through and render an EMPTY warn panel on 892 live
 * accounts: a banner that shouts "something is wrong with your data" and then says nothing. Falling back
 * to `null` renders nothing at all, which is the honest failure.
 */
const FREEZE_KEY: Partial<Record<PlanState, TKey>> = {
  lapsed_trial: "planState.freeze.lapsedTrial",
  paid_expired: "planState.freeze.paidExpired",
  expired_other: "planState.freeze.expiredOther",
  cancelled: "planState.freeze.cancelled",
};

export function PlanFreezeNotice({
  plan_state,
  plan_badge,
  base_type,
  className,
}: PlanFreezeNoticeProps) {
  const { t } = useTranslation();

  if (!isPlanNotLive(plan_state)) return null;

  // ── round 3, ruling 2 — the account badges Active while plan_state itself says not-live ───────────
  // A LITERAL check against `"active"`, exactly like accountStatusOf's own whitelist — never coerced,
  // never re-derived. Anything else (absent, null, free/expired/unknown) falls straight through to the
  // classic branch below, byte-identical to every payload that predates this ruling.
  if (plan_badge === "active") {
    const phoneOnly = typeof base_type === "string" && PHONE_ONLY_BASE_RE.test(base_type);
    return (
      <p className={noticeClassName("warn", className)}>
        {t(phoneOnly ? "planState.freeze.activePhoneOnly" : "planState.freeze.activeNotLive")}
      </p>
    );
  }

  const state = planStateOf(plan_state);

  // No sentence for this state → say nothing. (Unreachable today: the four keys above are exactly the
  // four states isPlanNotLive admits. It is here so that it STAYS unreachable.)
  const key = FREEZE_KEY[state];
  if (!key) return null;

  // paid_expired is the ALERT (danger); the rest are quiet FACTS (warn — information, not an error).
  // Matches the badge tones, so the panel and the pill can never disagree about how urgent this is.
  const alert = state === "paid_expired";

  return <p className={noticeClassName(alert ? "danger" : "warn", className)}>{t(key)}</p>;
}
